import { useSearchParams, Link } from 'react-router-dom';
import { grandTourContent } from '../content/read-the-tour';

/**
 * ReadTheTour — /read route (Plan 07-03 closes FALL-01).
 *
 * A fully accessible text-and-photo walkthrough of the Grand Tour. No Canvas,
 * no WebGL, no pointer-lock, no mouse required. Tab-navigable; screen-reader
 * landmark-compliant; axe-core clean (vitest-axe assertion lives in the
 * companion test file; Playwright + @axe-core sweep lives in Plan 07-05).
 *
 * Content lives in `src/content/read-the-tour.ts` so Plan 07-04 (Sources page)
 * and Plan 07-02 (narration) can cross-reference the section IDs without
 * importing the component. This file is pure markup + routing glue.
 *
 * Capability-gate reason banner (FALL-02 hand-off from main.tsx):
 *   - `?reason=no-webgl2` | `low-mem-touch` | `context-failure` is rendered
 *     as a `role="status"` live-region element at the top of the <main>.
 *     No role=status when the page is reached directly without a reason.
 *
 * Escape hatch: the first interactive element on the page is a
 * "Try the 3D Experience" link to `/` so a keyboard user who landed here
 * accidentally can leave with one tab + enter.
 */
const REASON_COPY: Record<string, string> = {
  'no-webgl2':
    "Your browser doesn’t support WebGL2, the graphics standard this 3D experience needs. The text-and-photo walkthrough below is equivalent to the 3D tour.",
  'low-mem-touch':
    'This device has limited memory. The text-and-photo walkthrough below is equivalent to the 3D tour.',
  'context-failure':
    "Your browser reports WebGL2 support but couldn’t create a graphics context. The text-and-photo walkthrough below is equivalent to the 3D tour.",
};

// AESTHETIC-BRIEF Rule 7 palette — matches SourcesPanel + EraToggle + Plan 07-01
// placeholder shell. Contrast pair (#3B2F2F on #FDF6EE) computes at ~13:1 —
// passes WCAG 2.2 SC 1.4.3 with headroom (07-RESEARCH.md Pitfall 8).
const CREAM = '#FDF6EE';
const ESPRESSO = '#3B2F2F';
const OCHRE = '#D4A87E';
const DIVIDER = '#E8D9C4';
const SERIF_STACK =
  '"Goudy Old Style", "Cormorant Garamond", Georgia, serif';

const STYLES: Record<string, React.CSSProperties> = {
  main: {
    minHeight: '100vh',
    background: CREAM,
    color: ESPRESSO,
    fontFamily: SERIF_STACK,
    padding: '3rem 1.5rem',
    boxSizing: 'border-box',
    lineHeight: 1.6,
  },
  container: {
    maxWidth: '72ch',
    margin: '0 auto',
  },
  escapeLink: {
    display: 'inline-block',
    marginBottom: '1rem',
    color: ESPRESSO,
    textDecoration: 'underline',
    fontSize: '0.95rem',
  },
  h1: {
    fontWeight: 400,
    fontSize: '2.25rem',
    letterSpacing: '0.04em',
    margin: '0.25rem 0 0.5rem',
    lineHeight: 1.2,
  },
  subtitle: {
    fontStyle: 'italic',
    opacity: 0.8,
    margin: '0 0 2rem',
    fontSize: '1.0625rem',
  },
  banner: {
    borderLeft: `3px solid ${OCHRE}`,
    background: '#F5E6D3',
    padding: '0.75rem 1rem',
    margin: '0 0 2rem 0',
    fontSize: '1.0625rem',
    lineHeight: 1.6,
  },
  section: {
    marginBottom: '2.5rem',
    borderBottom: `1px solid ${DIVIDER}`,
    paddingBottom: '1.5rem',
  },
  h2: {
    fontWeight: 400,
    fontSize: '1.5rem',
    letterSpacing: '0.02em',
    margin: '0 0 0.75rem',
    lineHeight: 1.3,
  },
  img: {
    width: '100%',
    height: 'auto',
    margin: '0.5rem 0 1rem',
    border: `1px solid ${DIVIDER}`,
    display: 'block',
  },
  p: {
    margin: '0.75rem 0',
    fontSize: '1.0625rem',
  },
  h3: {
    fontWeight: 600,
    fontSize: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    margin: '1.25rem 0 0.5rem',
  },
  sourceList: {
    paddingLeft: '1.25rem',
    margin: 0,
  },
  sourceLink: {
    color: ESPRESSO,
    textDecoration: 'underline',
  },
};

export function ReadTheTour() {
  const [params] = useSearchParams();
  const reason = params.get('reason');
  const bannerCopy = reason ? REASON_COPY[reason] : undefined;

  return (
    <main
      role="main"
      aria-labelledby="read-title"
      style={STYLES.main}
    >
      <div style={STYLES.container}>
        <Link to="/" style={STYLES.escapeLink}>
          Try the 3D Experience
        </Link>

        <h1 id="read-title" style={STYLES.h1}>
          Edgewood Lake: A Walk Around the 1925 Shore
        </h1>
        <p style={STYLES.subtitle}>
          A text-and-photo walkthrough of the vanished resort in Shades Valley, Alabama.
        </p>

        {bannerCopy && (
          <div role="status" style={STYLES.banner}>
            {bannerCopy}
          </div>
        )}

        {grandTourContent.map((section) => (
          <section
            key={section.id}
            aria-labelledby={`${section.id}-heading`}
            style={STYLES.section}
          >
            <h2 id={`${section.id}-heading`} style={STYLES.h2}>
              {section.heading}
            </h2>
            <img
              src={section.vistaPhotoUrl}
              alt={section.vistaPhotoAlt}
              style={STYLES.img}
              loading="lazy"
            />
            {section.paragraphs.map((p, i) => (
              <p key={i} style={STYLES.p}>
                {p}
              </p>
            ))}
            <h3 style={STYLES.h3}>Sources</h3>
            <ul style={STYLES.sourceList}>
              {section.sourceIds.map((sid) => (
                <li key={sid}>
                  <Link to={`/sources#${sid}`} style={STYLES.sourceLink}>
                    {sid}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </main>
  );
}
